import React, { useMemo, useState } from 'react';
import { Button, Card, Empty, Modal, Popconfirm, Select, Table, Tag, Typography, Upload, Space, message } from 'antd';
import { InboxOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useGetOrderLineItemDocumentsQuery,
  useUploadOrderLineItemDocumentsMutation,
  useDeleteOrderLineItemDocumentMutation,
  useCopyOrderLineItemDocumentsMutation,
  useLazyGetOrderLineItemsQuery,
} from '../../../redux/api/order-line-item';

const { Dragger } = Upload;
const { Title } = Typography;

type Props = {
  lineItemId: string;
};

const DOC_TYPES = ['Setup Sheets', 'Notes', 'Other'];

export default function OrderLineItemDocuments({ lineItemId }: Props) {
  const { data: docs, refetch, isFetching } = useGetOrderLineItemDocumentsQuery(lineItemId, {
    skip: !lineItemId,
  });

  const [uploadType, setUploadType] = useState<string>('Setup Sheets');
  const [uploadDocs, { isLoading: isUploading }] = useUploadOrderLineItemDocumentsMutation();
  const [deleteDoc, { isLoading: isDeleting }] = useDeleteOrderLineItemDocumentMutation();
  const [copyDocs, { isLoading: isCopying }] = useCopyOrderLineItemDocumentsMutation();

  const [copyOpen, setCopyOpen] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  const [targetSearch, setTargetSearch] = useState('');
  const [targetOptions, setTargetOptions] = useState<{ label: string; value: string }[]>([]);
  const [selectedTargets, setSelectedTargets] = useState<string[]>([]);
  const [getLineItems, { isFetching: isSearching }] = useLazyGetOrderLineItemsQuery();

  const columns = useMemo(
    () => [
      {
        title: 'File Name',
        render: (r: any) => (
          <a href={r.file} target="_blank" rel="noreferrer">{r?.name || r.filename}</a>
        ),
      },
      {
        title: 'Upload Date',
        render: (r: any) => (r.created_time ? dayjs(r.created_time).format('YYYY-MM-DD HH:mm') : '-'),
      },
      {
        title: 'Uploaded By',
        render: (r: any) => r?.created_by?.full_name || r?.created_by?.email || '-',
      },
      {
        title: 'Type',
        render: (r: any) => r?.type || '-',
      },
      {
        title: 'Actions',
        align: 'right' as const,
        render: (r: any) => (
          <Popconfirm
            title="Delete this document from this job?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={async () => {
              await deleteDoc({ id: lineItemId, fileId: r.id }).unwrap();
              refetch();
            }}
          >
            <Button type="text" icon={<DeleteOutlined />} loading={isDeleting} />
          </Popconfirm>
        ),
      },
    ],
    [deleteDoc, isDeleting, lineItemId, refetch]
  );

  // Let the server enforce duplicate naming and return a friendly display name.

  const onUploadChange = async (info: any) => {
    const file = info?.file;
    if (!file) return false;
    const formData = new FormData();
    formData.append('type', uploadType);

    const rawFile: File = (file?.originFileObj as File) || (file as File);
    formData.append('file', rawFile as any);
    try {
      await uploadDocs({ id: lineItemId, formData }).unwrap();
      refetch();
    } catch (e) {
      message.error('Failed to upload document');
    }
    return false;
  };

  const onSearchTargets = async (value: string) => {
    setTargetSearch(value);
    if (!value || value.length < 2) {
      setTargetOptions([]);
      return;
    }
    try {
      const res: any = await getLineItems({ page: 1, search: value, partsbadger_production_method: 'Swiss' }).unwrap();
      const options = (res?.results || [])
        .filter((li: any) => li.id !== lineItemId)
        .map((li: any) => ({ label: `${li.order?.name || ''} — ${li.name}`, value: li.id }));
      setTargetOptions(options);
    } catch (e) {
      setTargetOptions([]);
    }
  };

  const openCopy = () => {
    setSelectedTargets([]);
    setCopyOpen(true);
  };

  const doCopy = async () => {
    try {
      await copyDocs({ document_ids: selectedDocIds, target_line_item_ids: selectedTargets }).unwrap();
      setCopyOpen(false);
      message.success('Documents copied');
    } catch (e) {
      message.error('Failed to copy documents');
    }
  };

  return (
    <Card className="mt-4" title={<div className="flex items-center justify-between w-full">
      <span>Documents</span>
      <Space>
        <Select
          value={uploadType}
          style={{ width: 180 }}
          onChange={(v) => setUploadType(v)}
          options={DOC_TYPES.map((t) => ({ label: t, value: t }))}
        />
        <Button onClick={openCopy} disabled={!docs || docs.length === 0 || selectedDocIds.length === 0}>
          Copy
        </Button>
      </Space>
    </div>}>
      <div className="mb-4">
        <Dragger multiple showUploadList={false} beforeUpload={() => false} onChange={onUploadChange} disabled={isUploading}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag files here to upload</p>
          <p className="ant-upload-hint">Support for a single or bulk upload.</p>
        </Dragger>
      </div>

      <Table
        size="small"
        rowKey={(r) => r.id}
        columns={columns as any}
        dataSource={docs || []}
        loading={isFetching}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No documents uploaded" /> }}
        pagination={false}
        rowSelection={{
          selectedRowKeys: selectedDocIds,
          onChange: (keys: React.Key[]) => setSelectedDocIds(keys as string[]),
        }}
      />

      <Modal
        title="Copy Documents to Jobs"
        open={copyOpen}
        onCancel={() => setCopyOpen(false)}
        onOk={doCopy}
        okButtonProps={{ disabled: selectedDocIds.length === 0 || selectedTargets.length === 0, loading: isCopying }}
      >
        <div className="mb-2">Select target job(s)</div>
        <Select
          mode="multiple"
          showSearch
          filterOption={false}
          onSearch={onSearchTargets}
          options={targetOptions}
          loading={isSearching}
          value={selectedTargets}
          onChange={(v) => setSelectedTargets(v as string[])}
          placeholder="Search by order or part name"
          style={{ width: '100%' }}
        />
        <div className="mt-3">
          <Tag color="blue">Selected Docs: {selectedDocIds.length}</Tag>
        </div>
      </Modal>
    </Card>
  );
}
